import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { FadeUp } from "./Reveal";
import { EmSwash } from "./EmSwash";
import { NoteEyebrow } from "./NoteEyebrow";
import { isPrototypeMode } from "./PrototypeNotice";
import { useDiscovery } from "../lib/publicCampaigns";
import { LiveCampaignCard } from "./browse/LiveCampaignCard";

/**
 * MeetTheStudents — landing-page section for the campaign grid.
 * Renders nothing until discovery is visible (enough live public campaigns
 * and the founder switch on). Rule-based lead, no hand-picking: the three
 * live public campaigns with a profile photo closest to goal (`lead_ids`).
 *
 * While REACT_APP_STUDENT_CAMPAIGNS_OPEN is not "true" the band is labelled
 * as a prototype: sample students, nothing live (founder rule 2026-09-06).
 * Copy never implies a donor funds a student directly: gifts go to My Rich
 * Aunt, which awards every grant.
 */
export const MeetTheStudents = () => {
  const { visible, items, count, lead_ids = [] } = useDiscovery();
  const lead = lead_ids.map((id) => items.find((c) => c.id === id)).filter(Boolean);
  if (!visible || lead.length === 0) return null;
  const proto = isPrototypeMode();
  return (
    <section id="students" data-testid="meet-the-students" data-prototype={proto ? "true" : "false"} className="relative overflow-hidden bg-sand py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <FadeUp>
          <NoteEyebrow accent="bg-grass">{proto ? "a preview of the student campaigns page" : "Meet the students"}</NoteEyebrow>
          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-end">
            <h2 className="font-serif text-3xl lg:text-[3.5rem] leading-[1.02] text-ink lg:col-span-7" data-testid="meet-the-students-heading">
              {proto ? <>Sample campaigns, <EmSwash color="text-grass">not live.</EmSwash></> : <>Real students, <EmSwash color="text-grass">live now.</EmSwash></>}
            </h2>
            <p className="text-base leading-relaxed text-olive lg:col-span-5" data-testid="meet-the-students-body">
              {proto
                ? "Once we launch, every student here will be enrollment-verified and every page read by a real person before it's published. Gifts are made to My Rich Aunt, which awards every grant."
                : "Enrollment-verified, and read by a real person before going live. Gifts are made to My Rich Aunt, which awards every grant; choosing a student is a non-binding preference."}
            </p>
          </div>
        </FadeUp>

        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {lead.map((c, i) => (
            <FadeUp key={c.id} delay={i * 0.08}>
              <LiveCampaignCard c={c} />
            </FadeUp>
          ))}
        </div>

        <FadeUp className="mt-16 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
          {!proto && (
            <p className="text-[13px] text-olive" data-testid="meet-the-students-count">
              {`${count} ${count === 1 ? "campaign is" : "campaigns are"} live right now.`}
            </p>
          )}
          <Link to="/campaigns" data-testid="meet-the-students-browse" className="inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-base font-semibold text-sand transition-colors hover:bg-forest sm:ml-auto">
            {proto ? "Preview the campaigns page" : "Browse all campaigns"} <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
          </Link>
        </FadeUp>
      </div>
    </section>
  );
};
